import { useEffect, useState } from 'react';
import { Alert, Link, Skeleton, Text } from '@backstage/ui';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import styles from './CatalogOverview.module.css';

/** Kinds worth surfacing on the landing page, in reading order. */
const KINDS = [
  { kind: 'Component', label: 'Components' },
  { kind: 'API', label: 'APIs' },
  { kind: 'System', label: 'Systems' },
  { kind: 'Resource', label: 'Resources' },
  { kind: 'Group', label: 'Teams' },
] as const;

/**
 * Live counts of what is actually in the catalog, each linking through to the
 * matching filtered view. Replaces the static "Getting Started" boilerplate.
 *
 * Reads the catalog's `entity-facets` endpoint directly, which returns every
 * kind and its count in a single request.
 */
export const CatalogOverview = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [counts, setCounts] = useState<Record<string, number>>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const baseUrl = await discoveryApi.getBaseUrl('catalog');
      const res = await fetchApi.fetch(`${baseUrl}/entity-facets?facet=kind`);
      if (!res.ok) {
        throw new Error(
          `Catalog request failed: ${res.status} ${res.statusText}`,
        );
      }
      const body = (await res.json()) as {
        facets?: Record<string, Array<{ value: string; count: number }>>;
      };
      // Kind casing varies by entity, so index case-insensitively.
      return Object.fromEntries(
        (body.facets?.kind ?? []).map(f => [f.value.toLowerCase(), f.count]),
      );
    };

    load().then(
      res => !cancelled && setCounts(res),
      err => !cancelled && setError(err),
    );

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  if (error) {
    return (
      <Alert
        status="danger"
        icon
        title="Catalog counts are unavailable"
        description={error.message}
      />
    );
  }

  return (
    <div className={styles.grid}>
      {KINDS.map(({ kind, label }) =>
        // A skeleton per tile while loading, so the grid keeps its shape rather
        // than collapsing to a single bar and jumping back.
        counts ? (
          <Link
            key={kind}
            className={styles.tile}
            href={`/?filters%5Bkind%5D=${kind.toLowerCase()}`}
          >
            <Text as="span" variant="title-medium" className={styles.count}>
              {counts[kind.toLowerCase()] ?? 0}
            </Text>
            <Text
              as="span"
              variant="body-x-small"
              color="secondary"
              className={styles.label}
            >
              {label}
            </Text>
          </Link>
        ) : (
          <Skeleton key={kind} height="72px" />
        ),
      )}
    </div>
  );
};
